import React, { useState, useCallback, useEffect } from 'react';
import { Text, Box, Button, Flex, Image } from 'rebass';
import { Formik, Form } from 'formik';
import { FiCheckCircle } from 'react-icons/fi';
import { Input } from '@rebass/forms';
// import ideasBackground from '@/components/app/IdeasBackground.svg';
import store from 'store';
import { useWindowSize } from 'react-use';
import { cancelUppyUpload, sendTrackingEvent } from '@/lib/helper';
import backgroundClassicImage from '@/public/Images/NewDesignImages/BackgroundClassicImage.png';
import { useDeleteCanceledClip } from '@/hooks/mutations';
import { ax, postEventDetails, postPrivateEventDetails } from '@/lib/api';
import { useCelebrationCoverPhoto, useLandScapeCheckHook } from '@/hooks/celebration';
import { useEntity } from '@/lib/entities';
// eslint-disable-next-line import/no-cycle
import { AddWebcamVideo } from '../VideoEditor/VideoEditorAddBar';
import AnimatedDialog from '../app/AnimatedDialog';
import ConfirmDialog from '../app/ConfirmDialog';
import { ButtonStyles } from '../Printables/ReusableComponents';
import { useFetchAuthToken } from '../Auth/index';
import UploadClipFile from '../VideoEditor/UploadClipFile';
import { useCreateConfirmation } from '../app/ConfirmationOverlay';
import SubmitButton from '../app/formik/SubmitButton';
// eslint-disable-next-line import/no-cycle
import ContributorEmailDialog from '../ContributorRecord/ContributorEmailDialog';
import OutlineButton from '../app/OutlineButton';
import ConfettiWrapper from '../ConfettiWrapper';
import VGBPreview from '../VGBPreview';

function cssHandling(isFromMobile) {
  if (isFromMobile)
    return `border-radius:0px; 
     overflow-y: auto !important;
    position: fixed;
    height:  100%;
    max-width: 100%;
    width: 100%;
    left: 0;
    top: 0;
    padding-top: 0px;
    margin-top:0px;
    background: #FDFDFD;`;
  return 'align-items: center; max-width: 800px;';
}

function Heading({ children, ...props }) {
  return (
    <Text
      sx={{
        fontFamily: 'Italianno !important',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '46px',
        lineHeight: '46px',
        textAlign: 'center',
      }}
      color="#414143"
      {...props}
    >
      {children}
    </Text>
  );
}

function AddNamesForm({ media, onDone, celebrationId, toNames, settoNames }) {
  const createConfirmation = useCreateConfirmation();
  const [error, setError] = useState(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState(false);

  const [fieldError, setFieldError] = useState(null);

  useEffect(() => {
    if (toNames && toNames.length === 0) setSubmitting(false);
  }, [toNames]);

  const handleSubmit = useCallback(async () => {
    try {
      if (toNames.length === 0) {
        setFieldError('Please add valid address');
        return false;
      }
      setError(null);
      setSubmitting(true);
      let status;
      if (Array.isArray(media)) {
        const response = await Promise.all(
          media.map((layerId) =>
            ax({ updatedToken: store.get('token') }).patch(
              `/v3/celebrations/${celebrationId}/${layerId.type}/${layerId.id}`,
              {
                data: {
                  attributes: {
                    name: toNames.join(', '),
                  },
                },
              }
            )
          )
        );
        status = response?.[0]?.status;
      } else {
        const response = await ax({ updatedToken: store.get('token') }).patch(
          `/v3/celebrations/${celebrationId}/${media.type}/${media.id}`,
          {
            data: {
              attributes: {
                name: toNames.join(', '),
              },
            },
          }
        );
        status = response?.status;
      }

      setSubmitting(false);
      setSubmitForm(false);
      if (status === 200) {
        createConfirmation({ icon: FiCheckCircle, message: 'Changes Saved' });
        onDone(toNames.join(', '));
      }
    } catch (err) {
      store.set('sentryError', `${err} AddnamesForm handlesubmit`);
    }
    return null;
  }, [toNames]);

  useEffect(() => {
    if (submitForm) handleSubmit();
  }, [submitForm]);

  function isInList(name) {
    return toNames.includes(name);
  }

  function isValid(name) {
    let newFieldError = null;

    if (isInList(name)) newFieldError = `${name} has already been added.`;

    if (!name) newFieldError = `Please enter a name`;

    if (newFieldError) {
      setFieldError(newFieldError);
      return false;
    }
    setFieldError(null);
    return true;
  }

  return (
    <Box width="100%" px="20px">
      <Formik initialValues={{ To: '' }} onSubmit={handleSubmit} enableReinitialize>
        {({ values, handleChange, setFieldValue }) => {
          function addNamesToForm(e) {
            e.preventDefault();
            e.stopPropagation();
            if (isValid(values.To)) {
              setFieldValue('To', '');
              settoNames([...toNames, values.To]);
            }
          }
          return (
            <Form>
              <Flex flexDirection="column" mt={3} width="100%">
                <Box width="100%">
                  {toNames.map((item) => (
                    <div className="tag-item" key={item}>
                      <span className="span">{item}</span>
                      <button
                        type="button"
                        className="button"
                        onClick={() => settoNames(toNames.filter((e) => e !== item))}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                  <Input
                    name="To"
                    placeholder="Type a name one at a time"
                    tabIndex={-1}
                    value={values.To}
                    sx={{ borderColor: fieldError ? 'error' : 'border' }}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      const { value } = e.target;
                      if (['Enter', 'Tab', ','].includes(e.key)) {
                        e.preventDefault();
                        if (isValid(value)) {
                          setFieldValue('To', '');
                          settoNames([...toNames, value]);
                        }
                      } else {
                        setFieldError(null);
                        setFieldValue('To', value);
                      }
                    }}
                  />
                  <Text color="error" p={1} fontSize={1}>
                    {fieldError}
                  </Text>
                  {error && (
                    <Box bg="pinks.1" p={2} mb={2}>
                      <Heading variant="headings.h5" color="pinks.4">
                        Failed to add names to {error}
                      </Heading>
                    </Box>
                  )}
                </Box>
                <Flex justifyContent="space-around">
                  <OutlineButton
                    color="purples.7"
                    height="44px"
                    width="40%"
                    mb={0}
                    variant="outline"
                    disabled={!values.To}
                    onClick={addNamesToForm}
                  >
                    Enter
                  </OutlineButton>
                  <SubmitButton
                    variant="special"
                    bg="purples.7"
                    sx={ButtonStyles}
                    m="auto"
                    height="46px"
                    width="40%"
                    disabled={(toNames.length === 0 || isSubmitting) && !values.To}
                    onClick={(e) => {
                      addNamesToForm(e);
                      setSubmitForm(true);
                    }}
                  >
                    Finish
                  </SubmitButton>
                </Flex>
              </Flex>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
}

function RenderContent({
  celebrationId,
  celebration,
  setCurrentModal,
  uploadSuccess,
  setUploadSuccess,
  allDone,
  setAllDone,
  isDisabled,
}) {
  const [toNames, settoNames] = useState([]);
  const coverPhoto = useCelebrationCoverPhoto(celebration?.id, 4);
  const templateDetails = useEntity(celebration?.['celebration-template']);

  const sendTrackingEventToServer = (event) => {
    postPrivateEventDetails(event, {
      'subject-type': 'Celebration',
      'subject-id': celebration?.id || celebrationId,
    });
  };

  if (allDone)
    return (
      <Flex
        justifyContent="center"
        flexDirection="column"
        maxWidth="500px"
        m="auto"
        mt={5}
        sx={{
          position: 'fixed',
          left: '8px',
          right: '8px',
        }}
      >
        <ConfettiWrapper>
          <Flex justifyContent="center" flexDirection="column" alignItems="center" mt={4}>
            <Text
              fontSize="18px"
              fontWeight={700}
              m="auto"
              textAlign="center"
              mb={2}
              lineHeight="150%"
              sx={{ fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.198px' }}
              color="#13C283"
            >
              HURRAY! we got your video.
            </Text>
            <Text
              fontSize="18px"
              fontWeight={400}
              m="auto"
              textAlign="center"
              mb={2}
              lineHeight="150%"
              sx={{ letterSpacing: '-0.396px' }}
              color="black"
            >
              Thanks so much for doing this!
            </Text>
            <Image src={backgroundClassicImage} width={['244px', '344px', '344px']} />
          </Flex>
        </ConfettiWrapper>
        {/* <Image src={VGBAlldone} /> */}
        <Button
          variant="special"
          bg="purples.7"
          mt={2}
          p={2}
          onClick={() => {
            setAllDone(false);
            settoNames([]);
            setUploadSuccess(false);
          }}
          sx={ButtonStyles}
        >
          Submit Another Video
        </Button>
      </Flex>
    );

  if (uploadSuccess)
    return (
      <ContributorEmailDialog
        templateDetails={templateDetails}
        ownerId={celebration?.creator?.id}
        onDone={(names) => setAllDone(names)}
        AddNamesForm={() =>
          AddNamesForm({
            celebrationId,
            media: uploadSuccess,
            onDone: (names) => setAllDone(names),
            toNames,
            settoNames,
          })
        }
      />
    );

  return (
    <VGBPreview
      celebration={celebration}
      coverPhoto={coverPhoto}
      recordVideo={() => {
        sendTrackingEventToServer('invitation_page_clicked_record_video_button');
        setCurrentModal('webcam');
      }}
      uploadVideo={() => {
        sendTrackingEventToServer('invitation_page_clicked_upload_from_library_button');
        setCurrentModal('video');
      }}
      isDisabled={isDisabled}
    />
  );
}

function PublicIdeasPage({
  celebration,
  queryParam,
  isFromInvitation,
  id,
  isDisabled,
  tokenStatus,
}) {
  const [currentModal, setCurrentModal] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const deleteCanceledClip = useDeleteCanceledClip(celebration?.id);

  const { width } = useWindowSize();
  const [isLandscape] = useLandScapeCheckHook();
  const isFromMobile = width < 500 || isLandscape;

  const fetchAuthToken = useFetchAuthToken();

  const closeModal = useCallback(
    (e) => {
      setUploadSuccess(e);
      setCurrentModal('');
    },
    [setCurrentModal]
  );

  const handleSubmit = async () => {
    const res = await ax().post('/v3/celebration_members');
    if (res.status === 200 || res.status === 201) {
      const {
        data: {
          data: {
            attributes: { id: memId, secret },
          },
        },
      } = res;
      await fetchAuthToken({ id: memId, secret });
      postEventDetails(`celebration_invitation_viewed${queryParam ? `&${queryParam}` : ''}`, {
        attributes: {
          'subject-type': 'Celebration',
          'subject-id': id,
        },
      });
    }
  };

  useEffect(() => {
    if ((isFromInvitation && !store.get('token')) || (store.get('token') && tokenStatus === 404)) {
      handleSubmit();
    }

    store.remove('Media Uploading');
  }, [isFromInvitation, tokenStatus]);

  const onDismiss = () => {
    if (store.get('Media Uploading')) {
      setShowConfirmClose(true);
    } else {
      if (showConfirmClose) {
        setShowConfirmClose(false);
      }
      setCurrentModal('');
    }
  };

  return (
    <Flex
      justifyContent="flex-start"
      alignItems="center"
      flexDirection={['column', 'column', 'row']}
    >
      {!isFromInvitation && (
        <Text
          sx={{
            border: '1px solid #000000',
            borderRadius: '15px',
            p: 2,
            // position: 'absolute',
            // left: 0,
            height: 'max-content',
            maxWidth: 346,
            mx: ['5%', '5%', '10%'],
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '130%',
          }}
        >
          Guests will see this once they scan your QR code on their mobile device.
        </Text>
      )}
      <Box
        sx={{
          height: !allDone && '625px',
          width: !allDone && '294px',
          // backgroundImage: !isFromInvitation && `url("${ideasBackground}")`,
          backgroundRepeat: 'no-repeat',
          // backgroundPosition: !isFromInvitation && 'inherit',
          m: isFromInvitation && 'auto',
        }}
      >
        <RenderContent
          celebrationId={id}
          celebration={celebration}
          uploadSuccess={uploadSuccess}
          allDone={allDone}
          setAllDone={setAllDone}
          isDisabled={isDisabled}
          setCurrentModal={setCurrentModal}
          setUploadSuccess={setUploadSuccess}
        />
        <AnimatedDialog
          title="Add Media Modal"
          isOpen={!!currentModal}
          contentProps={{ width: isFromMobile ? '100vw' : '80vw' }}
          contentCSS={cssHandling(isFromMobile)}
          onDismiss={onDismiss}
          mt={currentModal === 'webcam' && isFromMobile ? '-20px' : undefined}
        >
          <ConfirmDialog
            isOpen={showConfirmClose}
            onConfirm={() => {
              cancelUppyUpload();
              if (store.get('Media Uploading')) {
                if (store.get('file-type')) {
                  sendTrackingEvent(`${store.get('file-type')}_upload_cancelled`, {
                    context: 'Canceled Upload',
                  });
                  store.remove('file-type');
                }
                deleteCanceledClip(store.get('uploadingID'));
                store.remove('Media Uploading');
              }
              setShowConfirmClose(false);
              setCurrentModal('');
            }}
            onDismiss={() => setShowConfirmClose(false)}
            title="Cancel upload?"
            body="Your file(s) will not be uploaded."
            cancelTitle="Nevermind"
            confirmTitle="Cancel upload"
            confirmVariant="destructive"
          />
          {currentModal === 'webcam' && (
            <AddWebcamVideo
              celebrationId={id}
              onFinished={closeModal}
              isFromVideoGuestBookInvitation
            />
          )}
          {currentModal === 'video' && (
            <UploadClipFile
              isFromVideoGuestBookInvitation
              type="Video or Image"
              celebrationId={id}
              onFinished={closeModal}
            />
          )}
        </AnimatedDialog>
      </Box>
    </Flex>
  );
}

export default PublicIdeasPage;
